/**
 * UserSettingsStore - Pure Functions
 * 
 * Pure, testable functions for per-user settings persistence.
 * This module contains no React dependencies - just pure logic.
 * 
 * @internal
 */

// ============ Constants ============

export const SCHEMA_VERSION = 1;
export const STORAGE_KEY_PREFIX = 'ax1:user_settings:v1:';

// ============ Type Definitions ============

export interface AppearanceSettings {
  themeMode: 'light' | 'dark' | 'system';
  density: 'compact' | 'comfortable' | 'spacious';
  homePage: 'dashboard' | 'projects' | 'tasks' | 'calendar';
  language: 'en' | 'en-gb' | 'fr' | 'de' | 'es';
  dateFormat: 'MM/DD/YYYY' | 'DD/MM/YYYY' | 'YYYY-MM-DD';
  animations: boolean;
  investorStartPage?: 'auto' | 'dashboard' | 'investments' | 'decisions' | 'data-rooms';
}

export interface EmailNotificationSettings {
  taskAssignments: boolean;
  deadlineReminders: boolean;
  projectUpdates: boolean;
  mentions: boolean;
  marketing: boolean;
}

export interface InAppNotificationSettings {
  taskUpdates: boolean;
  comments: boolean;
  documentUpdates: boolean;
}

export interface NotificationTimingSettings {
  taskReminder: '1' | '2' | '3' | '7';
  milestoneReminder: '1' | '2' | '3' | '7';
}

export interface QuietHoursSettings {
  enabled: boolean;
  start: string; // HH:MM format
  end: string;   // HH:MM format
}

export interface NotificationSettings {
  email: EmailNotificationSettings;
  inApp: InAppNotificationSettings;
  timing: NotificationTimingSettings;
  digest: 'immediate' | 'daily' | 'weekly';
  quietHours: QuietHoursSettings;
}

export interface ProfileSettings {
  fullName: string;
  department: string;
  company: string;
  phone: string;
}

export interface SecuritySettings {
  loginNotifications: boolean;
}

export interface UserSettings {
  _version: number;
  _lastUpdated: string;
  appearance: AppearanceSettings;
  notifications: NotificationSettings;
  profile: ProfileSettings;
  security: SecuritySettings;
}

export type DeepPartial<T> = {
  [P in keyof T]?: T[P] extends object ? DeepPartial<T[P]> : T[P];
};

// ============ Default Values ============

export const DEFAULT_APPEARANCE_SETTINGS: AppearanceSettings = {
  themeMode: 'system',
  density: 'comfortable',
  homePage: 'dashboard',
  language: 'en',
  dateFormat: 'MM/DD/YYYY',
  animations: true,
};

export const DEFAULT_EMAIL_NOTIFICATIONS: EmailNotificationSettings = {
  taskAssignments: true,
  deadlineReminders: true,
  projectUpdates: true,
  mentions: true,
  marketing: false,
};

export const DEFAULT_INAPP_NOTIFICATIONS: InAppNotificationSettings = {
  taskUpdates: true,
  comments: true,
  documentUpdates: true,
};

export const DEFAULT_NOTIFICATION_TIMING: NotificationTimingSettings = {
  taskReminder: '1',
  milestoneReminder: '1',
};

export const DEFAULT_QUIET_HOURS: QuietHoursSettings = {
  enabled: false,
  start: '22:00',
  end: '08:00',
};

export const DEFAULT_NOTIFICATION_SETTINGS: NotificationSettings = {
  email: DEFAULT_EMAIL_NOTIFICATIONS,
  inApp: DEFAULT_INAPP_NOTIFICATIONS,
  timing: DEFAULT_NOTIFICATION_TIMING,
  digest: 'immediate',
  quietHours: DEFAULT_QUIET_HOURS,
};

export const DEFAULT_PROFILE_SETTINGS: ProfileSettings = {
  fullName: '',
  department: '',
  company: '',
  phone: '',
};

export const DEFAULT_SECURITY_SETTINGS: SecuritySettings = {
  loginNotifications: true,
};

// ============ Pure Functions ============

/**
 * Deep merge two objects, with source overriding target.
 * Objects are recursively merged; primitives and arrays are replaced.
 */
export function deepMerge<T extends Record<string, unknown>>(target: T, source: Partial<T>): T {
  const result = { ...target };
  
  for (const key in source) {
    if (Object.prototype.hasOwnProperty.call(source, key)) {
      const sourceValue = source[key];
      const targetValue = target[key];
      
      if (
        sourceValue !== null &&
        typeof sourceValue === 'object' &&
        !Array.isArray(sourceValue) &&
        targetValue !== null &&
        typeof targetValue === 'object' &&
        !Array.isArray(targetValue)
      ) {
        result[key] = deepMerge(
          targetValue as Record<string, unknown>,
          sourceValue as Record<string, unknown>
        ) as T[typeof key];
      } else if (sourceValue !== undefined) {
        result[key] = sourceValue as T[typeof key];
      }
    }
  }
  
  return result;
}

/**
 * Get storage key for a user
 */
export function getStorageKey(userId: string): string {
  return `${STORAGE_KEY_PREFIX}${userId}`;
}

/**
 * Get default user settings with current timestamp
 * Returns deep copies of nested objects to prevent mutation of constants
 */
export function getDefaultUserSettings(): UserSettings {
  return {
    _version: SCHEMA_VERSION,
    _lastUpdated: new Date().toISOString(),
    appearance: { ...DEFAULT_APPEARANCE_SETTINGS },
    notifications: {
      email: { ...DEFAULT_EMAIL_NOTIFICATIONS },
      inApp: { ...DEFAULT_INAPP_NOTIFICATIONS },
      timing: { ...DEFAULT_NOTIFICATION_TIMING },
      digest: DEFAULT_NOTIFICATION_SETTINGS.digest,
      quietHours: { ...DEFAULT_QUIET_HOURS },
    },
    profile: { ...DEFAULT_PROFILE_SETTINGS },
    security: { ...DEFAULT_SECURITY_SETTINGS },
  };
}

/**
 * Safely parse stored settings JSON, returning null on failure
 */
export function safeParseSettings(raw: string | null): Partial<UserSettings> | null {
  if (!raw) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw);
    // Basic validation: must be an object
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
      return null;
    }
    return parsed as Partial<UserSettings>;
  } catch {
    return null;
  }
}

/**
 * Merge saved settings with defaults, ensuring all required fields exist
 */
export function mergeWithDefaults(saved: Partial<UserSettings> | null): UserSettings {
  if (!saved) {
    return getDefaultUserSettings();
  }
  const defaults = getDefaultUserSettings();
  const merged = deepMerge(
    defaults as unknown as Record<string, unknown>, 
    saved as Record<string, unknown>
  ) as unknown as UserSettings;
  merged._version = SCHEMA_VERSION;
  return merged;
}

/**
 * Apply a partial patch to current settings
 */
export function applyPatch(current: UserSettings, patch: DeepPartial<UserSettings>): UserSettings {
  const updated = deepMerge(
    current as unknown as Record<string, unknown>, 
    patch as Record<string, unknown>
  ) as unknown as UserSettings;
  updated._lastUpdated = new Date().toISOString();
  return updated;
}

// ============ Storage Interface ============

export interface StorageAdapter {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/**
 * Create a user settings store with a given storage adapter
 * This allows for easy testing with mock storage
 */
export function createUserSettingsStore(storage: StorageAdapter) {
  const cache = new Map<string, UserSettings>();
  
  return {
    /**
     * Load user settings, merging with defaults for any missing fields
     */
    load(userId: string): UserSettings {
      // Check cache first
      if (cache.has(userId)) {
        return cache.get(userId)!;
      }
      
      try {
        const key = getStorageKey(userId);
        const stored = storage.getItem(key);
        const parsed = safeParseSettings(stored);
        
        if (!parsed) {
          // No stored settings or parse failed, return defaults
          const defaults = getDefaultUserSettings();
          cache.set(userId, defaults);
          return defaults;
        }
        
        // Handle version migration if needed
        if (parsed._version !== SCHEMA_VERSION) {
          console.info(`[UserSettingsStore] Migrating settings from v${parsed._version} to v${SCHEMA_VERSION}`);
        }
        
        // Deep merge with defaults to ensure all fields exist
        const merged = mergeWithDefaults(parsed);
        
        cache.set(userId, merged);
        return merged;
        
      } catch (error) {
        console.error('[UserSettingsStore] Error loading settings:', error);
        return getDefaultUserSettings();
      }
    },
    
    /**
     * Save a partial update to user settings
     */
    savePatch(userId: string, patch: DeepPartial<UserSettings>): UserSettings {
      const current = this.load(userId);
      const updated = applyPatch(current, patch);
      
      const key = getStorageKey(userId);
      storage.setItem(key, JSON.stringify(updated));
      cache.set(userId, updated);
      
      return updated;
    },
    
    /**
     * Save complete user settings (replaces existing)
     */
    save(userId: string, settings: UserSettings): void {
      const key = getStorageKey(userId);
      settings._lastUpdated = new Date().toISOString();
      storage.setItem(key, JSON.stringify(settings));
      cache.set(userId, settings);
    },
    
    /**
     * Clear user settings (for logout/reset)
     */
    clear(userId: string): void {
      const key = getStorageKey(userId);
      storage.removeItem(key);
      cache.delete(userId);
    },
    
    /**
     * Clear cache (for testing or memory management)
     */
    clearCache(): void {
      cache.clear();
    },
  };
}
